// ---------------------------------------------------------
// Datei: Toast.tsx
// Zweck: Toast-Notification-Component für Feedback
// Besonderheiten:
// - Erscheint unten rechts (Mobile: unten zentriert)
// - Auto-Hide nach 3 Sekunden
// - 4 Typen: success, error, warning, info
// - Slide-in Animation von unten
// - Safe-Area-Insets für Mobile
// ---------------------------------------------------------

import React, { useEffect } from 'react';

export type { ToastType } from '../types/ui';
import type { ToastType } from '../types/ui';

export interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  duration?: number; // in ms, default 3000
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ 
  id, 
  type, 
  message, 
  duration = 3000, 
  onClose 
}) => {
  // Auto-Close nach duration
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);
    
    return () => clearTimeout(timer);
  }, [id, duration, onClose]);
  
  // Typ-spezifische Styles
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-500 dark:bg-green-600',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          )
        };
      case 'error':
        return {
          bg: 'bg-red-500 dark:bg-red-600',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )
        };
      case 'warning':
        return {
          bg: 'bg-amber-500 dark:bg-amber-600',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )
        };
      case 'info':
        return {
          bg: 'bg-blue-500 dark:bg-blue-600',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
      default:
        return {
          bg: 'bg-gray-500',
          icon: null
        };
    }
  };
  
  const styles = getTypeStyles();
  
  return (
    <div
      className={`
        ${styles.bg} text-white
        rounded-lg shadow-lg
        px-4 py-3
        flex items-center gap-3
        min-w-[280px] max-w-[400px]
        animate-slide-up
        mb-2
      `}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      {styles.icon && (
        <div className="flex-shrink-0">
          {styles.icon}
        </div>
      )}
      
      {/* Message */}
      <div className="flex-1 text-sm font-medium">
        {message}
      </div>
      
      {/* Close Button */}
      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 hover:bg-white/20 rounded p-1 transition-colors"
        aria-label="Toast schließen"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default Toast;
